import { AGGREGATE_NAMES, LOAN_REQUEST_RESOLVERS } from 'cpool-constants';
import type { SearchMode, SearchType } from 'cpool-constants';
import { schemaLoanRequestResolver_Find } from 'cpool-tables/backend-resolvers';
import { useDebounce } from 'use-debounce';

import type { SearchField } from '../search/contexts/SearchContext';
import { useAggregateInfiniteQuery } from '~/queryUtils/useQuery';
import { useQueryPositionStore } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';

const PAGE_SIZE = 5;

export const useFind = ({
    searchFields,
    searchMode,
    searchQuery,
    searchType,
}: {
    searchQuery: string | null;
    searchFields: SearchField;
    searchMode: SearchMode;
    searchType: SearchType;
}) => {
    const queryKey = queryKeys.find();
    const queryPosition = useQueryPositionStore((state) => state.positions[JSON.stringify(queryKey)]);
    const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

    const trimmed = (debouncedSearchQuery ?? '').trim();
    const hasTextQuery = trimmed.length > 0;
    const hasSearchFields = Object.values(searchFields).some((field) => (field ?? '').trim().length > 0);
    const enabled = hasTextQuery || hasSearchFields;

    const query = useAggregateInfiniteQuery({
        acl: {},
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        enabled,
        pageSize: PAGE_SIZE,
        resolverName: LOAN_REQUEST_RESOLVERS.find,
        schema: schemaLoanRequestResolver_Find,
        searchParams: {
            filter: false,
            position: queryPosition,
            query: debouncedSearchQuery,
            searchFields,
            searchMode,
            searchType,
        },
    });

    return {
        data: query.data?.pages.flat() ?? [],
        fetchNextPage: query.fetchNextPage,
        hasNextPage: query.hasNextPage,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
    };
};
